import React, { FunctionComponent, ReactNode, useRef, useState } from 'react'
import { Grid, Text, Icon, Tooltip, CARET_PLACEMENTS, useIsDesignLanguage, DesignLanguage, Divider, GUTTER_VARIANT } from '@afterpaytouch/core'
import styles from './style.module.css'
import {
  useAppRouter,
  useCalculatedPaymentSchedules,
  useCheckoutState,
  useCrossBorderTrade,
  useInitialData,
  useIsCheckoutLoadingOrUpdating,
  useIsDeferredShipping,
  useIsEstimatedPPA,
  useIsExpress,
  useIsSupOrder,
  useShippingOption,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { Money, TotalAmountPayable } from '..'
import { Trans } from '../../utils/convergence'
import { useTranslation } from '../../utils/convergence'
import { AddressModeType, nonShippingInfoAddressModes } from '../../model'
import { isNullOrUndefined } from '@afterpay/utils'
import { useIsOrderBreakdownVisible, useOrderBreakdownType } from './hooks'
import { Money as MoneyObject } from '@afterpay/types'
import { useMoney } from '../Money/useMoney'

const GridItem = Grid.Item
const GridFixedItem = Grid.FixedItem

interface OrderBreakdownProps {
  name: string
  amount?: MoneyObject
  loading?: boolean
  preferFreeText?: boolean
}

const OrderBreakdownItem: FunctionComponent<OrderBreakdownProps> = ({ name, amount, loading, preferFreeText = false }): JSX.Element => {
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { locale } = useAppRouter()
  const { isZeroAmount } = useMoney({ locale })

  const testNamespace = `order-breakdown-${name}`
  const itemTextSize = isArcadeDesignLanguage ? textSize : smallTextSize
  const textColor = isArcadeDesignLanguage ? 'Standard' : 'Subtle'
  const amountShowing =
    isZeroAmount(amount?.amount) && preferFreeText ? (
      <Text size={itemTextSize} testNameSpace={`${testNamespace}-free`} color={textColor}>
        {t('summary:free')}
      </Text>
    ) : (
      <Money loading={loading} color={textColor} locale={locale} size={itemTextSize} value={amount} testNameSpace={testNamespace} />
    )

  return (
    <Grid justifyContent='space-between'>
      <GridItem>
        <Text size={itemTextSize} color={textColor}>
          {name}
        </Text>
      </GridItem>
      <GridFixedItem align='right'>{amountShowing}</GridFixedItem>
    </Grid>
  )
}

/**
 * This component is used to planned GA design
 */
export const OrderBreakdown = (): JSX.Element => {
  const { started, checkout } = useCheckoutState()
  const { locale } = useAppRouter()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const { smallTextSize, textSize, extraSmallTextSize } = useCheckoutSizes()
  const { t } = useTranslation()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const isExpressCheckout = useIsExpress()
  const { addressMode } = useInitialData()
  const { isCrossBorder } = useCrossBorderTrade()
  const { selectedShippingOption: nonXbtSelectedShippingOption, xbtAllShippingOptions } = useShippingOption()
  const isShippingOptionUpdating = useIsCheckoutLoadingOrUpdating()
  const [showDiscountTooltip, setShowDiscountTooltip] = useState(false)
  const discountTooltipRef = useRef<HTMLButtonElement>(null)
  const { formatMoney } = useMoney({
    locale,
    currency: started?.promotion?.discount.currency,
    decimalPlaces: 2,
  })

  const purchaseAmount = checkout?.amount
  const selectedShippingOption = isCrossBorder
    ? xbtAllShippingOptions.find((option) => option.id === nonXbtSelectedShippingOption.id)
    : nonXbtSelectedShippingOption
  const shippingAmount = selectedShippingOption?.shippingAmount
  const taxAmount = selectedShippingOption?.taxAmount
  const orderBreakdownType = useOrderBreakdownType(shippingAmount, taxAmount)
  const orderBreakdownIsVisible = useIsOrderBreakdownVisible(orderBreakdownType)

  const isEstimatedPpaOrder = useIsEstimatedPPA()
  const isDeferredShipping = useIsDeferredShipping()
  const isExpress = useIsExpress()
  const { initial } = useCheckoutState()
  const isSupOrder = useIsSupOrder()
  const isExpressWithoutBuyNow = isExpress && !initial.buyNow
  const deferringShippingAddressMode = isDeferredShipping && !nonShippingInfoAddressModes.includes(addressMode)
  const isEstimatedDeferredShippingOrder = deferringShippingAddressMode || isExpressWithoutBuyNow
  const isEstimatedTotal = isEstimatedPpaOrder || isEstimatedDeferredShippingOrder || isSupOrder

  const renderExpressCheckoutItem = (): ReactNode => {
    if (!isExpressCheckout || isNullOrUndefined(selectedShippingOption)) {
      return null
    }

    switch (addressMode) {
      case AddressModeType.ADDRESS_WITH_SHIPPING_OPTIONS:
        return (
          <>
            <OrderBreakdownItem name={t('summary:shipping')} amount={shippingAmount} loading={isShippingOptionUpdating} preferFreeText />
            <OrderBreakdownItem name={t('summary:taxes')} amount={taxAmount} loading={isShippingOptionUpdating} />
          </>
        )
      case AddressModeType.SHIP_TO_ORDER_ADDRESS:
        return (
          <>
            <OrderBreakdownItem name={t('summary:shipping')} amount={shippingAmount} loading={isShippingOptionUpdating} preferFreeText />
            <OrderBreakdownItem name={t('summary:taxes')} amount={taxAmount} loading={isShippingOptionUpdating} />
          </>
        )
      case AddressModeType.PICKUP_FROM_ORDER_ADDRESS:
        return (
          <>
            <OrderBreakdownItem name={t('summary:handling')} amount={shippingAmount} loading={isShippingOptionUpdating} preferFreeText />
            <OrderBreakdownItem name={t('summary:taxes')} amount={taxAmount} loading={isShippingOptionUpdating} />
          </>
        )
    }
  }

  const toogleDisplayDiscountTooltip = (): void => {
    setShowDiscountTooltip(!showDiscountTooltip)
  }

  const renderDiscountTooltip = (): ReactNode => {
    if (!showDiscountTooltip) {
      return null
    }

    const tooltipContent = (): ReactNode => (
      <Text size={smallTextSize}>
        {t('summary:discountTooltip', {
          amount: formatMoney(started?.promotion?.discount.amount),
        })}
      </Text>
    )

    return (
      <Tooltip
        id='order-breakdown-discount-tooltip'
        triggerRef={discountTooltipRef}
        caretPlacement={CARET_PLACEMENTS.TOP_START}
        usePortal={false}
        content={tooltipContent()}
        skidding={65}
        hasCaret={true}
        useMobileView={false}
        bubbleSize={{ width: '100%' }}
        closeOnOutsideClick={true}
        onClose={toogleDisplayDiscountTooltip}
      />
    )
  }

  const renderDiscountItem = (): ReactNode => {
    const discount = started?.promotion?.discount
    if (isNullOrUndefined(discount)) {
      return null
    }

    const discountTextSize = isArcadeDesignLanguage ? textSize : smallTextSize
    const textColor = isArcadeDesignLanguage ? 'Standard' : 'Subtle'
    const moneyColor = isArcadeDesignLanguage ? 'Success' : 'Moss Mid'
    return (
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text size={discountTextSize} color={textColor}>
            {t('summary:discount')}
            <button
              tabIndex={0}
              ref={discountTooltipRef}
              className={styles.discountTooltipIcon}
              onClick={toogleDisplayDiscountTooltip}
              data-testid='discount-tooltip-icon'
            >
              <Icon.Info size='Small' color={textColor} />
            </button>
          </Text>
        </GridItem>
        <GridFixedItem align='right'>
          <Text color={moneyColor} size={discountTextSize} renderAs='span'>
            -
          </Text>
          <Money color={moneyColor} locale={locale} size={discountTextSize} value={discount} />
        </GridFixedItem>
      </Grid>
    )
  }

  const renderDueTodayItem = (): ReactNode => {
    return (
      <Grid justifyContent='space-between'>
        <GridItem>
          <Text renderAs='span' size={textSize} bold>
            <Trans i18nKey='orders:orderSummary.downPaymentDueToday' />
          </Text>{' '}
          <div className={styles.wrappedOneOfX}>
            <Text renderAs='span' size={textSize} bold>
              {t('orders:orderSummary.oneOfX', { total: paymentSchedules.length })}
            </Text>
          </div>
        </GridItem>
        <GridFixedItem align='right'>
          <Money bold showAsterisk={isEstimatedTotal} loading={isShippingOptionUpdating} locale={locale} size={textSize} value={paymentSchedules[0].amount} />
        </GridFixedItem>
      </Grid>
    )
  }

  const renderDisclaimer = (): ReactNode => {
    if (!isEstimatedTotal) {
      return null
    }

    return (
      <Grid verticalGutter={GUTTER_VARIANT.ONE}>
        <GridItem>
          <Text size={extraSmallTextSize} color='Subtle'>
            {t('orders:orderSummary.estimatedTotalDisclaimer')}
          </Text>
        </GridItem>
      </Grid>
    )
  }

  const renderDivider = (): ReactNode => (
    <Grid>
      <GridItem>
        <Divider kind='Hairline' margin='Half' />
      </GridItem>
    </Grid>
  )

  const renderFullOrderBreakdown = (): ReactNode => (
    <>
      <OrderBreakdownItem loading={isShippingOptionUpdating} name={t('orders:orderSummary.orderAmount')} amount={purchaseAmount} />
      {renderDiscountItem()}
      {renderExpressCheckoutItem()}
      {renderDivider()}
    </>
  )

  // TODO: Implement renderDonation if Arcade layout becomes available outside US.
  const renderDiscountAndDonationOrderBreakdown = (): ReactNode => (
    <>
      {renderDiscountItem()}
      {renderDivider()}
    </>
  )

  const renderOrderBreakdown = (): ReactNode => {
    switch (orderBreakdownType) {
      case 'Full':
        return renderFullOrderBreakdown()
      case 'DiscountAndDonation':
        return renderDiscountAndDonationOrderBreakdown()
      default:
        return null
    }
  }

  return (
    <div className={styles.container}>
      {renderOrderBreakdown()}
      <TotalAmountPayable isEstimatedTotal={isEstimatedTotal} orderBreakdownIsHidden={!orderBreakdownIsVisible} />
      {renderDueTodayItem()}
      {renderDisclaimer()}
      {renderDiscountTooltip()}
    </div>
  )
}
